import { resetMobileViewportAfterLogin } from "../app/appNavigationController";
import React, { useCallback, useEffect, useState } from "react";

const PASSWORD_POLICY_REGEX = /^.{4,30}$/;
const PASSWORD_POLICY_MESSAGE = "A nova senha deve ter de 4 a 30 caracteres. Pode conter letras, números e caracteres especiais.";

export default function LoginScreen({ farms, onLogin, deps = {} }) {
  const {
    syncFarmCollaboratorAccountsToEmployees,
    getAccounts,
    deriveAllowedModules,
    normalizeModulePermissions,
    normalizeCostCenterPermissions,
    normalizeUiPreferences,
    ensureDefaultAdminAccount,
    saveAccounts,
    pushGlobal,
    DB,
    ensureAdminAccountAvailable,
    getPersistedAdminCredentialState,
    readAdminPasswordOverride,
    stripDeletedItems,
    isFarmArchived,
    T,
    FarmEmoji,
    PwInput,
    Btn,
    Input,
  } = deps;

const[mode,setMode]=useState(null);
  const[selectedFarm,setSelectedFarm]=useState(null);
  const[farmUsers,setFarmUsers]=useState([]);
  const[selectedUser,setSelectedUser]=useState(null);
  const[password,setPassword]=useState("");
  const[adminUser,setAdminUser]=useState("");
  const[adminPass,setAdminPass]=useState("");
  const[error,setError]=useState("");
  const[installNotice,setInstallNotice]=useState("");
  const[loading,setLoading]=useState(false);
  const[firstAccessFlow,setFirstAccessFlow]=useState(null);
  const[firstAccessPw,setFirstAccessPw]=useState({ password:"", confirm:"", error:"", saving:false });

  useEffect(()=>{
    try{
      const msg=localStorage.getItem("fz:server_install_notice")||"";
      if(msg){
        setInstallNotice(msg);
        localStorage.removeItem("fz:server_install_notice");
      }
    }catch(_){ }
  },[]);

  const loadUsers = useCallback(()=>{
    if(!selectedFarm) return;
    syncFarmCollaboratorAccountsToEmployees(selectedFarm.id).catch(()=>{});
    getAccounts().then(accs=>{
      const sol=accs.filter(a=>a.role==="solicitante"&&a.active&&(a.farmIds||[]).includes(selectedFarm.id));
      setFarmUsers(sol);
    });
  }, [getAccounts, selectedFarm, syncFarmCollaboratorAccountsToEmployees]);

  // Load collab users when farm selected
  useEffect(()=>{
    if(!selectedFarm) return;
    const onSyncUpdated=(e)=>{
      if(!e.detail?.key||e.detail.key==="auth:accounts") loadUsers();
    };
    loadUsers();
    window.addEventListener("fz:sync:updated", onSyncUpdated);
    return ()=>window.removeEventListener("fz:sync:updated", onSyncUpdated);
  },[loadUsers, selectedFarm]);

  function finalizeAuthenticatedLogin(acc, farm=null){
    const baseSession = {
      id:acc.id,
      username:acc.username,
      role:acc.role,
      name:acc.name,
      farmIds:acc.farmIds||[],
      active:acc.active,
      collabLevel:acc.collabLevel||1,
      allowedModules:deriveAllowedModules(acc.modulePermissions,acc.role),
      allowedCards:acc.allowedCards||[],
      modulePermissions:normalizeModulePermissions(acc.modulePermissions,acc.allowedModules,acc.role),
      costCenterPermissions:normalizeCostCenterPermissions(acc.costCenterPermissions,acc.role),
      canManagePurchases: acc.role === "admin" || acc.role === "gestor" ? true : !!acc.canManagePurchases,
      canApprovePurchaseWithoutQuote:acc.role==="admin"?acc.canApprovePurchaseWithoutQuote!==false:!!acc.canApprovePurchaseWithoutQuote,
      receivePurchaseNotifications: acc.role === "admin" ? true : !!acc.receivePurchaseNotifications,
      uiPreferences:normalizeUiPreferences(acc.uiPreferences),
      mustChangePassword:acc.mustChangePassword===true,
      temporaryPassword:acc.temporaryPassword===true,
    };
    resetMobileViewportAfterLogin();
    setTimeout(()=>onLogin(baseSession, acc.role==="solicitante" ? farm : null), 40);
  }

  async function completeFirstAccess(){
    const nextPassword = String(firstAccessPw.password || "").trim();
    const confirmPassword = String(firstAccessPw.confirm || "").trim();
    if(!firstAccessFlow?.accountId){
      setFirstAccessPw(f=>({...f,error:"Conta não encontrada."}));
      return;
    }
    if(nextPassword !== confirmPassword){
      setFirstAccessPw(f=>({...f,error:"As senhas não coincidem."}));
      return;
    }
    if(!PASSWORD_POLICY_REGEX.test(nextPassword)){
      setFirstAccessPw(f=>({...f,error:PASSWORD_POLICY_MESSAGE}));
      return;
    }
    setFirstAccessPw(f=>({...f,error:"",saving:true}));
    try{
      const accs = await getAccounts();
      const updated = accs.map(a => String(a?.id||"") !== String(firstAccessFlow.accountId)
        ? a
        : { ...a, password: nextPassword, mustChangePassword:false, temporaryPassword:false, updatedAt:new Date().toISOString(), updatedBy:a?.name || a?.username || "system" });
      const ensuredUpdated = ensureDefaultAdminAccount(updated, firstAccessFlow?.accountName || "system");
      await saveAccounts(ensuredUpdated);
      try{
        const res=await pushGlobal("auth:accounts",ensuredUpdated,firstAccessFlow?.accountName||"system");
        if(res?.version)await DB.set("auth:accounts:_ver",res.version);
      }catch(_){ }
      try{ window.dispatchEvent(new CustomEvent("fz:sync:updated",{detail:{key:"auth:accounts"}})); }catch(_){ }
      const fresh = ensuredUpdated.find(a=>String(a?.id||"")===String(firstAccessFlow.accountId)) || firstAccessFlow.account;
      setFirstAccessFlow(null);
      setFirstAccessPw({ password:"", confirm:"", error:"", saving:false });
      finalizeAuthenticatedLogin({ ...fresh, mustChangePassword:false, temporaryPassword:false }, firstAccessFlow?.farm || null);
    }catch(_){
      setFirstAccessPw(f=>({...f,error:"Não foi possível atualizar a senha agora.",saving:false}));
      return;
    }
  }

  async function loginAdmin(e){
    e?.preventDefault();
    setLoading(true); setError("");
    try{
      const accs=await ensureAdminAccountAvailable("login-admin");
      const normalizedAdminUser = adminUser.trim().toLowerCase();
      const adminRecord = accs.find(a=>String(a?.username||"").trim().toLowerCase()==="admin");
      const persistedAdmin = await getPersistedAdminCredentialState();
      const adminPasswordCanonical = String(
        adminRecord?.password || persistedAdmin?.password || readAdminPasswordOverride() || "admin1979"
      ).trim();
      const adminDefaultBlocked = adminRecord?.defaultPasswordBlocked === true || persistedAdmin?.defaultPasswordBlocked === true;
      let acc=accs.find(a=>String(a?.username||"").trim().toLowerCase()===normalizedAdminUser&&a.password===adminPass&&a.active);
      if(!acc && normalizedAdminUser === "admin" && adminRecord?.active !== false && adminPass === adminPasswordCanonical){
        acc = { ...adminRecord, password: adminPasswordCanonical, role: "admin", active: true };
      }
      if(normalizedAdminUser === "admin" && adminPass === "admin1979" && adminDefaultBlocked){
        setError("A senha padrão do admin foi desativada.");setLoading(false);return;
      }
      if(!acc){setError("Usuário ou senha incorretos.");setLoading(false);return;}
      if(acc.role==="admin" || acc.role==="gestor"){
        if(acc.mustChangePassword===true || acc.temporaryPassword===true){
          setFirstAccessFlow({ accountId:acc.id, account:acc, farm:null, accountName:acc.name||acc.username||"system" });
          setFirstAccessPw({ password:"", confirm:"", error:"", saving:false });
        } else {
          finalizeAuthenticatedLogin(acc, null);
        }
      }
      else {setError("Acesso negado para este perfil.");setLoading(false);}
    }catch(e){setError("Erro ao autenticar.");setLoading(false);}
    setLoading(false);
  }

  async function loginCollab(){
    setLoading(true); setError("");
    if(!selectedUser){setError("Selecione um colaborador.");setLoading(false);return;}
    try{
      const accs=await getAccounts();
      const acc=accs.find(a=>a.id===selectedUser.id);
      if(!acc||!acc.active){setError("Conta inativa ou não encontrada.");setLoading(false);return;}
      if(acc.password!==password){setError("Senha incorreta.");setLoading(false);return;}
      if(acc.mustChangePassword===true || acc.temporaryPassword===true){
        setFirstAccessFlow({ accountId:acc.id, account:acc, farm:selectedFarm, accountName:acc.name||acc.username||"system" });
        setFirstAccessPw({ password:"", confirm:"", error:"", saving:false });
      } else {
        finalizeAuthenticatedLogin(acc, selectedFarm);
      }
    }catch(e){setError("Erro ao autenticar.");setLoading(false);}
    setLoading(false);
  }

  const visibleActiveFarms = stripDeletedItems(farms || []).filter(f=>!isFarmArchived(f));

  return(
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"20px 16px",fontFamily:"'DM Sans',sans-serif"}}>
      <div style={{width:"100%",maxWidth:420}}>
        {/* Logo */}
        <div style={{textAlign:"center",marginBottom:36}}>
          <div style={{display:"flex",justifyContent:"center",marginBottom:4}}>
            <img src="/native-assets/login-nelore.png" alt="Nelore" width={560} height={389} loading="eager" fetchPriority="high" decoding="async" style={{width:180,height:"auto",marginBottom:8,filter:"drop-shadow(0 4px 16px rgba(0,0,0,0.18))"}} />
          </div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:700,color:T.text}}>Trembo</div>
          <div style={{fontSize:13,color:T.muted,marginTop:4}}>Sistema de Gestão</div>
        </div>

        {installNotice&&(<div style={{background:"rgba(245,158,11,0.14)",border:"1px solid rgba(245,158,11,0.45)",color:T.text,borderRadius:14,padding:"12px 14px",fontSize:13,lineHeight:1.45,marginBottom:14}}><div style={{fontWeight:800,marginBottom:4}}>Nova instalação detectada</div><div>{installNotice}</div><button type="button" onClick={()=>setInstallNotice("")} style={{marginTop:8,background:"transparent",border:"none",color:T.accent,cursor:"pointer",fontWeight:700,padding:0}}>Entendi</button></div>)}

        {!mode&&(
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontSize:13,color:T.muted,textAlign:"center",marginBottom:8}}>Como deseja entrar?</div>
            {[["solicitante","👤","Colaborador(a)","Criar pedidos"],["gestor","🔐","Gestor / Admin","Gerenciar fazendas"]].map(([m,icon,label,sub])=>(
              <div key={m} onClick={()=>setMode(m)}
                style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px",cursor:"pointer",display:"flex",alignItems:"center",gap:14,transition:"all .2s"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=T.accent;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;}}>
                <div style={{fontSize:28}}>{icon}</div>
                <div><div style={{fontWeight:700,color:T.text,fontSize:15}}>{label}</div><div style={{fontSize:12,color:T.muted,marginTop:2}}>{sub}</div></div>
                <div style={{marginLeft:"auto",color:T.muted,fontSize:18}}>›</div>
              </div>
            ))}
          </div>
        )}

        {mode==="solicitante"&&<>
          {!selectedFarm&&(
            <div>
              <div style={{fontSize:14,fontWeight:600,color:T.text,marginBottom:12}}>Selecione a fazenda:</div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {visibleActiveFarms.map(f=>(
                  <div key={f.id} onClick={()=>setSelectedFarm(f)}
                    style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"14px 18px",cursor:"pointer",display:"flex",alignItems:"center",gap:12}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=f.color||T.accent}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
                    <FarmEmoji farm={f} size={24} />
                    <div style={{fontWeight:700,color:T.text}}>{f.name}</div>
                    <div style={{marginLeft:"auto",color:T.muted}}>›</div>
                  </div>
                ))}
              </div>
              <button onClick={()=>setMode(null)} style={{width:"100%",marginTop:16,background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:13}}>← Voltar</button>
            </div>
          )}
          {selectedFarm&&(
            <div>
              <div style={{textAlign:"center",marginBottom:16}}>
                <FarmEmoji farm={selectedFarm} size={32} />
                <div style={{fontWeight:700,color:T.text,marginTop:4}}>{selectedFarm.name}</div>
              </div>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:13,color:T.muted,marginBottom:8}}>Selecione seu nome:</div>
                <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:200,overflowY:"auto"}}>
                  {farmUsers.map(u=>(
                    <div key={u.id} onClick={()=>setSelectedUser(u)}
                      style={{padding:"10px 14px",borderRadius:10,cursor:"pointer",fontWeight:selectedUser?.id===u.id?700:400,
                        border:`2px solid ${selectedUser?.id===u.id?T.accent:T.border}`,
                        background:selectedUser?.id===u.id?`${T.accent}12`:T.card2,
                        color:selectedUser?.id===u.id?T.text:T.muted}}>
                      👤 {u.name}
                    </div>
                  ))}
                  {farmUsers.length===0&&<div style={{color:T.muted,fontSize:13,textAlign:"center",padding:16}}>Nenhum colaborador cadastrado para esta fazenda.</div>}
                </div>
              </div>
              {selectedUser&&(
                <div style={{marginBottom:12}}>
                  <PwInput value={password} onChange={e=>setPassword(e.target.value)} placeholder="Senha" onKeyDown={e=>e.key==="Enter"&&loginCollab()}/>
                </div>
              )}
              {error&&<div style={{color:T.red,fontSize:12,marginBottom:8,textAlign:"center"}}>{error}</div>}
              <Btn full onClick={loginCollab} disabled={loading||!selectedUser}>
                {loading?<span style={{display:"inline-flex",alignItems:"center",gap:8,justifyContent:"center"}}><span style={{width:14,height:14,border:"2px solid rgba(255,255,255,.30)",borderTopColor:"#fff",borderRadius:"50%",display:"inline-block",animation:"fzSpin .9s linear infinite"}} />Entrando...</span>:"Entrar"}
              </Btn>
              <button onClick={()=>{setSelectedFarm(null);setSelectedUser(null);setPassword("");setError("");}} style={{width:"100%",marginTop:10,background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:13}}>← Trocar fazenda</button>
            </div>
          )}
        </>}

        {mode==="gestor"&&(
          <div>
            <div style={{marginBottom:12}}>
              <Input value={adminUser} onChange={e=>setAdminUser(e.target.value)} placeholder="Usuário" onKeyDown={e=>e.key==="Enter"&&document.getElementById("admin-pass-input")?.focus()}/>
            </div>
            <div style={{marginBottom:12}}>
              <PwInput id="admin-pass-input" value={adminPass} onChange={e=>setAdminPass(e.target.value)} placeholder="Senha" onKeyDown={e=>e.key==="Enter"&&loginAdmin()}/>
            </div>
            {error&&<div style={{color:T.red,fontSize:12,marginBottom:8,textAlign:"center"}}>{error}</div>}
            <Btn full onClick={loginAdmin} disabled={loading}>{loading?<span style={{display:"inline-flex",alignItems:"center",gap:8,justifyContent:"center"}}><span style={{width:14,height:14,border:"2px solid rgba(255,255,255,.30)",borderTopColor:"#fff",borderRadius:"50%",display:"inline-block",animation:"fzSpin .9s linear infinite"}} />Entrando...</span>:"Entrar"}</Btn>
            <button onClick={()=>{setMode(null);setError("");}} style={{width:"100%",marginTop:10,background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:13}}>← Voltar</button>
          </div>
        )}


        {firstAccessFlow&&(
          <div style={{marginTop:18,background:T.card,border:`2px solid ${T.accent}`,borderRadius:16,padding:18,boxShadow:"0 16px 40px rgba(0,0,0,.18)"}}>
            <div style={{fontSize:22,textAlign:"center",marginBottom:8}}>🔐</div>
            <div style={{fontWeight:800,color:T.text,fontSize:18,textAlign:"center",marginBottom:6}}>Primeiro acesso</div>
            <div style={{fontSize:12,color:T.muted,textAlign:"center",lineHeight:1.5,marginBottom:14}}>Defina sua nova senha para continuar. Ela deve ter de 4 a 30 caracteres e pode conter letras, números e caracteres especiais.</div>
            <div style={{display:"grid",gap:10}}>
              <PwInput value={firstAccessPw.password} onChange={e=>setFirstAccessPw(f=>({...f,password:e.target.value,error:""}))} placeholder="Nova senha" onKeyDown={e=>e.key==="Enter"&&completeFirstAccess()} />
              <PwInput value={firstAccessPw.confirm} onChange={e=>setFirstAccessPw(f=>({...f,confirm:e.target.value,error:""}))} placeholder="Confirmar nova senha" onKeyDown={e=>e.key==="Enter"&&completeFirstAccess()} />
              {firstAccessPw.error&&<div style={{color:T.red,fontSize:12,textAlign:"center"}}>{firstAccessPw.error}</div>}
              <Btn full onClick={completeFirstAccess} disabled={firstAccessPw.saving}>{firstAccessPw.saving?"Salvando...":"Salvar e entrar"}</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
